import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Clock, DollarSign, TrendingUp, Shield, Zap, CheckCircle2 } from 'lucide-react';

export const DedicatedTeam = () => {
  const benefits = [
    { icon: Users, title: 'Seamless Integration', description: 'Our developers work as an extension of your in-house team' },
    { icon: Clock, title: 'Flexible Scaling', description: 'Scale your team up or down based on project requirements' },
    { icon: DollarSign, title: 'Cost Effective', description: 'Save up to 60% compared to traditional hiring' },
    { icon: TrendingUp, title: 'Faster Delivery', description: 'Accelerate project timelines with dedicated resources' },
    { icon: Shield, title: 'Full Control', description: 'Maintain complete oversight of your development process' },
    { icon: Zap, title: 'Quick Onboarding', description: 'Start within 2 weeks with pre-vetted professionals' }
  ];

  const models = [
    { title: 'Fixed Team', description: 'A stable team dedicated exclusively to your project', features: ['Long-term commitment','Consistent team members','Predictable monthly costs','Best for ongoing projects'], ideal: 'Ideal for: Long-term projects, product development' },
    { title: 'Flexible Team', description: 'Adjust team size based on project phases and requirements', features: ['Scale up or down','Pay for what you need','Adapt to changing needs','Best for dynamic projects'], ideal: 'Ideal for: Projects with varying requirements' },
    { title: 'Hybrid Team', description: 'Combine your in-house team with our remote developers', features: ['Augment existing team','Fill skill gaps','Knowledge transfer','Best for scaling quickly'], ideal: 'Ideal for: Rapid scaling, specialized skills' }
  ];

  const process = [
    'Share your requirements and project goals',
    'We assemble a custom team with the right skills',
    'Meet and interview the proposed team members',
    'Onboard and integrate with your processes',
    'Start development with continuous communication'
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Dedicated Team | Novaseeds Technologies</title>
        <meta name="description" content="Build your extended development team with Novaseeds Technologies. Dedicated developers, flexible engagement models, seamless integration, and full control." />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">

          {/* Hero */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-20">
            <span className="text-sm uppercase tracking-widest text-muted-foreground font-bold">DEDICATED TEAMS</span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mt-4 mb-6">
              <span className="text-foreground">Build Your Extended</span><br />
              <span className="text-gradient">Development Team</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              Get a fully managed team of experts working exclusively on your project. Complete transparency, direct communication, and full control.
            </p>
          </motion.div>

          {/* Benefits */}
          <section className="mb-32">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">Why Choose a Dedicated Team?</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 rounded-3xl hover:shadow-lg transition-all hover:-translate-y-1 group">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <Icon size={28} className="text-blue-600" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-medium text-foreground mb-3">{benefit.title}</h3>
                    <p className="text-base md:text-lg leading-relaxed text-muted-foreground">{benefit.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Engagement Models */}
          <section className="mb-32">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">Flexible Engagement Models</h2>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto">Choose the model that fits your business needs</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {models.map((model, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 rounded-3xl hover:shadow-lg transition-all hover:-translate-y-1">
                  <h3 className="text-2xl font-semibold text-foreground mb-3">{model.title}</h3>
                  <p className="text-muted-foreground mb-6">{model.description}</p>
                  <ul className="space-y-3 mb-6">
                    {model.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-muted-foreground text-sm">
                        <CheckCircle2 size={16} className="text-blue-600 flex-shrink-0 mt-1" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-blue-800 text-sm font-medium">{model.ideal}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* How It Works */}
          <section className="mb-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">How We Build<br /><span className="text-gradient">Your Team</span></h2>
                <div className="space-y-6">
                  {process.map((step, index) => (
                    <motion.div key={index} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start space-x-4">
                      <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold">{index + 1}</span>
                      </div>
                      <p className="text-base md:text-lg leading-relaxed text-muted-foreground pt-1">{step}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <img src="https://images.unsplash.com/photo-1630673470267-417e4d361129?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Team collaboration" className="rounded-3xl w-full h-auto shadow-2xl"/>
              </motion.div>
            </div>
          </section>

          {/* CTA */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-blue-50 border border-blue-200 rounded-3xl p-12 text-center">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">Ready to Scale Your Development?</h2>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto mb-8">
              Let's discuss how a dedicated team can accelerate your project
            </p>
            <Link to="/contact" className="inline-block bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-3 font-semibold shadow-[0_2px_8px_rgba(37,99,235,0.25)] transition-all hover:scale-105 active:scale-95">
              Build Your Team
            </Link>
          </motion.div>

        </div>
      </div>
    </>
  );
};
